// Page names for the top bar and the browser tab.
//
// The breadcrumb comes from the route (navigation.js getBreadcrumbs); a record
// page adds the record's own name once it has loaded (usePageRecord), so the
// trail reads "Contacts › Ada Lovelace" rather than "Contacts › Detail". The
// tab title follows the same trail (WCAG 2.4.2). Public pages, outside the
// workspace shell, set their tab title with useDocumentTitle.
import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

export const APP_NAME = "NexusDialer";

let current = { path: null, record: null };
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Name the record on screen. `record` is a label ("Ada Lovelace") or a list of
 * crumbs ([{ label: "Roles", path: "/organization/people/roles" }, { label: "Admin" }]).
 */
export const usePageRecord = (record) => {
  const { pathname } = useLocation();
  const key = Array.isArray(record) ? record.map((c) => `${c.label}|${c.path || ""}`).join("/") : record || "";
  useEffect(() => {
    if (!key) return undefined;
    current = { path: pathname, record };
    emit();
    return () => {
      if (current.path === pathname) {
        current = { path: null, record: null };
        emit();
      }
    };
    // `key` stands for `record`, which is often a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, key]);
};

/** The record named for this path, or null. */
export const usePageRecordFor = (pathname) =>
  useSyncExternalStore(
    subscribe,
    () => (current.path === pathname ? current.record : null),
    () => null,
  );

/** Replace a generic last crumb ("Detail") with the record, or append it. */
export const withRecord = (crumbs, record, pathname) => {
  if (!record) return crumbs;
  const extra = (Array.isArray(record) ? record : [{ label: record }]).map((crumb) => ({
    label: crumb.label,
    path: crumb.path || pathname,
  }));
  const trail = [...crumbs];
  if (trail.length > 1 && trail[trail.length - 1].label === "Detail") trail.pop();
  return [...trail, ...extra];
};

/** "Ada Lovelace · Contacts · NexusDialer" from a trail. */
export const titleFromCrumbs = (crumbs) => {
  const labels = crumbs.map((c) => c.label).filter((label) => label && label !== APP_NAME);
  return [...labels.reverse(), APP_NAME].join(" · ");
};

/** Set the tab title for a page outside the workspace shell. */
export const useDocumentTitle = (title) => {
  useEffect(() => {
    if (!title) return;
    document.title = title === APP_NAME || title.endsWith(` · ${APP_NAME}`) ? title : `${title} · ${APP_NAME}`;
  }, [title]);
};
